import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { ScrollFadeIn } from "@/components/ScrollFadeIn";
import { ArrowRight } from "lucide-react";

function FeaturedCard({ article, index }: { article: any; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(cardRef, { once: true, margin: "-80px" });

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  // First card is large, rest are regular
  const isLarge = index === 0;

  return (
    <motion.div
      ref={cardRef}
      className={isLarge ? "md:col-span-2 md:row-span-2" : ""}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{
        duration: 0.7,
        delay: index * 0.15,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      <Link to={`/cases/${article.slug}`} className="group block h-full">
        <div
          className={`relative overflow-hidden ${
            isLarge ? "aspect-[4/5] md:aspect-[3/4]" : "aspect-[3/4]"
          }`}
        >
          {article.hero_image_url ? (
            <motion.img
              src={article.hero_image_url}
              alt={article.project_name}
              className="absolute inset-0 h-[112%] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
              style={{ y: imgY }}
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-muted text-muted-foreground text-sm font-sans">
              No image
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-700" />

          {/* Text overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
            <h3 className="font-serif text-lg md:text-xl text-white leading-snug">
              {article.project_name}
            </h3>
            <div className="flex items-center gap-2 mt-2 font-sans text-[11px] uppercase tracking-[0.18em] text-white/70">
              {article.client_name && <span>{article.client_name}</span>}
              {article.client_name && article.location && (
                <span className="text-white/40">·</span>
              )}
              {article.location && <span>{article.location}</span>}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function FeaturedCasesSection() {
  const { data: articles, isLoading } = useQuery({
    queryKey: ["featured-cases"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_articles")
        .select("id, slug, project_name, hero_image_url, client_name, location, category")
        .eq("published", true)
        .is("deleted_at", null)
        .order("display_order", { ascending: true, nullsFirst: false })
        .limit(3);

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) return null;
  if (!articles || articles.length === 0) return null;

  return (
    <section className="relative py-20 md:py-28 lg:py-36 bg-secondary/40">
      {/* Soft top/bottom fade edges */}
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-background to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent" />

      <div className="container relative mx-auto px-4">
        {/* Section Header */}
        <ScrollFadeIn>
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide text-foreground leading-tight">
              Cases
            </h2>
            <Link
              to="/cases"
              className="group hidden md:flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              View all
              <ArrowRight
                size={14}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </ScrollFadeIn>

        {/* Cases Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {articles.map((article, index) => (
            <FeaturedCard key={article.id} article={article} index={index} />
          ))}
        </div>

        {/* Mobile "View all" link */}
        <ScrollFadeIn>
          <div className="mt-10 text-center md:hidden">
            <Link
              to="/cases"
              className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              View all cases
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}
